import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/**
 * Tema visual del usuario (§5.5 SRS).
 *
 * - 'light' / 'dark' / 'system' (hereda preferencia del SO).
 * - El valor efectivo se aplica como atributo `data-theme="light"|"dark"` en <html>,
 *   y el CSS responde a `[data-theme="dark"]` para sobrescribir variables.
 * - Se persiste localmente y se sincroniza al backend (PATCH /api/me/preferences)
 *   cuando hay sesión. La sincronización vive en el AppShell para no acoplar este
 *   provider a la capa de auth.
 */

export type ThemeChoice = 'light' | 'dark' | 'system'
export type ThemeEffective = 'light' | 'dark'

type ThemeContextValue = {
  choice: ThemeChoice
  effective: ThemeEffective
  setChoice: (choice: ThemeChoice) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)
const STORAGE_KEY = 'intrepid.theme'

function readSystem(): ThemeEffective {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveEffective(choice: ThemeChoice): ThemeEffective {
  return choice === 'system' ? readSystem() : choice
}

function applyToDom(effective: ThemeEffective): void {
  if (typeof document === 'undefined') return
  document.documentElement.dataset.theme = effective
  // Actualizar también theme-color del meta para que iOS pinte la status bar correcta.
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', effective === 'dark' ? '#0f172a' : '#ffffff')
  }
}

function readStoredChoice(): ThemeChoice {
  if (typeof window === 'undefined') return 'system'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  return 'system'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readStoredChoice())
  const [effective, setEffective] = useState<ThemeEffective>(() => resolveEffective(readStoredChoice()))

  // Aplicar al DOM en cada cambio.
  useEffect(() => {
    applyToDom(effective)
  }, [effective])

  // Cuando el modo es 'system', escuchar cambios del SO.
  useEffect(() => {
    if (choice !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => setEffective(readSystem())
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [choice])

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next)
    setEffective(resolveEffective(next))
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // localStorage puede fallar en modo privado de algunos browsers
    }
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, effective, setChoice }),
    [choice, effective, setChoice],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme debe usarse dentro de <ThemeProvider>.')
  return ctx
}
